if (!window.customElements.get('custom-one-time-single-v2')) {
  window.customElements.define(
    'custom-one-time-single-v2',
    class CustomOneTimeSingleV2 extends window.HTMLElement {
      connectedCallback() {
        this.initDOMElements();
        this.boundHandleSubmit = this.handleSubmit.bind(this);
        this.boundHandleLinkClick = this.handleLinkClick.bind(this);
        this.initEventListeners();
        this.updateCheckoutLink();
        this.initCountdown();
      }

      disconnectedCallback() {
        this.removeEventListeners();
        this.stopCountdown();
      }

      initDOMElements() {
        this.form = this.querySelector('form');
        this.button = this.querySelector('[id$="-order-button"]');
        this.useAddToCart = this.dataset.useAddToCart === 'true';
        this.enableGtmTracking = this.dataset.enableGtmTracking === 'true';
        this.goToCheckout = this.form?.hasAttribute('data-go-to-checkout') ?? false;
        this.variantIdInput = this.form?.querySelector('input[name="id"]');
        this.countdownEl = this.querySelector('[data-countdown]');
      }

      initEventListeners() {
        if (this.useAddToCart) {
          this.form?.addEventListener('submit', this.boundHandleSubmit);
          return;
        }

        this.button?.addEventListener('click', this.boundHandleLinkClick);
      }

      removeEventListeners() {
        this.form?.removeEventListener('submit', this.boundHandleSubmit);
        this.button?.removeEventListener('click', this.boundHandleLinkClick);
      }

      initCountdown() {
        if (!this.countdownEl) {
          return;
        }

        const endDate = this.dataset.endDate;
        const endHour = Number.parseInt(this.dataset.endHour || '23', 10);

        if (!endDate || !/^\d{2}\/\d{2}\/\d{4}$/.test(endDate)) {
          this.countdownEl.hidden = true;
          return;
        }

        const [day, month, year] = endDate.split('/').map(Number);
        this.endTime = new Date(year, month - 1, day, endHour, 0, 0);

        if (this.endTime <= new Date()) {
          this.countdownEl.hidden = true;
          return;
        }

        this.updateCountdown();
        this.countdownIntervalId = window.setInterval(() => this.updateCountdown(), 1000);
      }

      updateCountdown() {
        if (!this.countdownEl || !this.endTime) {
          return;
        }

        const diff = this.endTime - Date.now();

        if (diff <= 0) {
          this.stopCountdown();
          this.countdownEl.hidden = true;
          return;
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        const daysEl = this.countdownEl.querySelector('[data-days]');
        const hoursEl = this.countdownEl.querySelector('[data-hours]');
        const minutesEl = this.countdownEl.querySelector('[data-minutes]');
        const secondsEl = this.countdownEl.querySelector('[data-seconds]');

        if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
        if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
        if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
        if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
      }

      stopCountdown() {
        if (this.countdownIntervalId) {
          window.clearInterval(this.countdownIntervalId);
          this.countdownIntervalId = null;
        }
      }

      handleLinkClick() {
        if (!this.enableGtmTracking || !window.SeranovaTracking) {
          return;
        }

        const href = this.button?.getAttribute('href');

        if (!href) {
          return;
        }

        this.button.href = this.appendTrackingParams(href);
      }

      async handleSubmit(event) {
        if (!this.useAddToCart) {
          return;
        }

        event.preventDefault();

        if (this.isSubmitting || !this.variantIdInput?.value) {
          return;
        }

        this.isSubmitting = true;

        try {
          this.form?.classList.add('is-loading');
          await this.addToCart({
            id: this.variantIdInput.value,
            quantity: 1,
          });

          if (this.goToCheckout) {
            window.location.href = '/checkout';
            return;
          }

          this.dispatchCartRefresh();
        } catch (error) {
          console.error('Failed to add item to cart:', error);
        } finally {
          this.form?.classList.remove('is-loading');
          this.isSubmitting = false;
        }
      }

      updateCheckoutLink() {
        if (this.useAddToCart || !this.button || this.button.tagName !== 'A') {
          return;
        }

        const href = this.button.getAttribute('href');

        if (!href || !this.enableGtmTracking || !window.SeranovaTracking) {
          return;
        }

        this.button.href = this.appendTrackingParams(href);
      }

      async addToCart(item) {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items: [item] }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message);
        }

        return response.json();
      }

      dispatchCartRefresh() {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('variant:add'));
      }

      appendTrackingParams(url) {
        if (!this.enableGtmTracking || !url || !window.SeranovaTracking) {
          return url;
        }

        return window.SeranovaTracking.appendParams(url);
      }
    },
  );
}
